/**
 * Deterministic, rule-based explanation of a reconciled driver block.
 * No model generates these sentences: every number comes from the inputs,
 * rounding is explicit, and missing pieces are stated rather than guessed.
 */
import { formatValue, formatChange, formatDiff, pctChange, kindOf } from "./format.js";

function changeText(label, from, to) {
  const diff = Number(to) - Number(from);
  const verb = diff >= 0 ? "raises" : "lowers";
  if (kindOf(label) === "rate") {
    return { verb, amount: formatChange(Math.abs(diff), { pp: true, decimals: 2 }).replace(/^\+/, "") };
  }
  const pct = pctChange(from, to);
  if (pct == null) return { verb: "moves", amount: formatDiff(label, diff) };
  return { verb, amount: formatChange(Math.abs(pct)).replace(/^\+/, "") };
}

function shareText(contribution, total) {
  if (!total) return "";
  const share = (contribution / total) * 100;
  if (!Number.isFinite(share)) return "";
  return ` (${Math.round(share)}% of the move)`;
}

/**
 * @param {object} rec  result of decomposition.reconcile()
 * @param {object} ctx  {series, week, date, scenario: 'alternative'|'baseline', basis}
 * @returns {string[]}  2–3 sentences, or a single sentence explaining why none can be written
 */
export function narrative(rec, ctx = {}) {
  const series = ctx.series || "the series";
  const week = ctx.week != null ? `week ${ctx.week}` : "the selected week";
  if (!rec) return [`No driver decomposition is available for ${series}.`];
  const out = [];
  const total = rec.total;
  const absTotal = Math.abs(total);
  const tiny = absTotal <= rec.tolerance;

  if (ctx.scenario === "baseline" || ctx.basis === "baseline-path") {
    out.push(`With no edited inputs, the ${week} forecast for ${series} equals the Janus baseline (${formatValue(series, rec.alternative)}).`);
    out.push("Edit one or more macro assumptions and click Apply to see which inputs move the forecast.");
    return out;
  }

  if (tiny) {
    out.push(`The alternative scenario leaves the ${week} forecast for ${series} unchanged at ${formatValue(series, rec.alternative)}.`);
    return out;
  }

  const ch = changeText(series, rec.baseline, rec.alternative);
  out.push(`The alternative scenario ${ch.verb} the ${week} forecast for ${series} by ${ch.amount}, from ${formatValue(series, rec.baseline)} to ${formatValue(series, rec.alternative)}.`);

  const same = rec.contributions.filter((c) => Math.sign(c.contribution) === Math.sign(total));
  const opp = rec.contributions.filter((c) => Math.sign(c.contribution) === -Math.sign(total) && c.contribution !== 0);
  if (same.length) {
    const top = same.slice(0, 2).map((c) => `${c.input} (${formatDiff(series, c.contribution)}${shareText(c.contribution, total)})`);
    out.push(`The largest modelled contributions are ${top.join(" and ")}.`);
  }
  if (opp.length) {
    const o = opp[0];
    out.push(`${o.input} works the other way (${formatDiff(series, o.contribution)}).`);
  }
  if (!rec.reconciled) {
    out.push(`Contributions do not fully reconcile: an unexplained residual of ${formatDiff(series, rec.residual)} remains (tolerance ${formatDiff(series, rec.tolerance)}).`);
  }
  out.push("Contributions are model-based accounting of the scenario change, not causal effects.");
  return out.slice(0, 4);
}
